import { ZButton } from "@/shared/components/button";
import FileUpload from "@/shared/components/custom/file-upload";
import { ZInput } from "@/shared/components/input";
import {
  ZModal,
  ZModalBody,
  ZModalContent,
  ZModalFooter,
  ZModalHeader,
} from "@/shared/components/modal";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";
import { UploadedFile } from "@/shared/types/file-upload";
import { Plus } from "lucide-react";

import { useLoggedInMember } from "@/modules/auth/states";
import { getZaniaTeamMemberRoles } from "@/modules/team/states/index.ts";
import { TeamRole } from "@/modules/team/types";
import { addNotification } from "@/shared/states/notification";
import { ApiErrorResponse, getApiErrorMessage, isValidationError } from "@/shared/utils/api-error-handler";
import { cn, Radio, RadioGroup, Select, SelectItem, Switch, Textarea, Tooltip } from "@nextui-org/react";
import { AxiosError } from "axios";
import { InfoIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EMAIL_REGEX } from "../../../../shared/constants/index.ts";
import { useGetFrameworksListQuery } from "../../requests/index.ts";
import {
  AGENT_TYPES,
  GapAssessmentTypes,
  RiskAssessmentTypes,
  UploadDialogState,
} from "../../types/index.ts";
import { helper } from "./helper.ts";
import { TPRAAssessmentFlow } from "./tpra";
import { containsProblematicCharacters, containsSpecialCharacters, getCharacterLimit, processDataBasedOnAssessment, startsWithSpecialCharacter } from "./util.ts";


// Define a type for the error messages
type ErrorMessages = {
  session?: string;
  [key: string]: string | undefined; // allows for dynamic keys
};

export default function UploadDialog({
  data,
  setIsOpen,
  onAddNewFramework,
}: {
  data: UploadDialogState;
  setIsOpen: (data: UploadDialogState) => void;
  onAddNewFramework?: (sessionTitle?: string) => void;
}) {
  const { isOpen, type } = data;

  // Delegate TPRA assessment type to dedicated flow component
  if (type === AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT) {
    const initialVendorData = data.vendorName
      ? {
          vendorName: data.vendorName,
          vendorDomain: data.vendorDomain,
          vendorLogoUrl: data.vendorLogoUrl,
        }
      : undefined;

    return (
      <TPRAAssessmentFlow
        isOpen={isOpen}
        onClose={() => setIsOpen({ isOpen: false, type: undefined })}
        initialVendorData={initialVendorData}
      />
    );
  }

  const loggedInMember = useLoggedInMember();

  const [helperData, setHelperData] = useState(() => {
    const initialHelper = (helper as any)[type as AGENT_TYPES];
    // For Gap Assessment, start with empty dropdown options
    if (type === AGENT_TYPES.GAP_ASSESSMENT) {
      return {
        ...initialHelper,
        dropdown: initialHelper?.dropdown ? { ...initialHelper.dropdown, options: [] } : undefined
      };
    }
    return initialHelper;
  });

  // Update helperData whenever type changes
  useEffect(() => {
    if (type) {
      const initialHelper = (helper as any)[type as AGENT_TYPES];
      // For Gap Assessment, start with empty dropdown options
      if (type === AGENT_TYPES.GAP_ASSESSMENT) {
        setHelperData({
          ...initialHelper,
          dropdown: initialHelper?.dropdown ? { ...initialHelper.dropdown, options: [] } : undefined
        });
      } else {
        setHelperData(initialHelper);
      }
    }
  }, [type]);

  const { data: customFrameworksList, refetch: refetchFrameworksList, isLoading: isLoadingFrameworks, isFetching: isFetchingFrameworks } = useGetFrameworksListQuery({ isEnabled: type === AGENT_TYPES.GAP_ASSESSMENT })
  const navigate = useNavigate();
  const memId = loggedInMember?.member_id || "";

  const {
    canAccessZaniaRiskAssessment,
    canAccessZaniaGapAssessment,
    canAccessZaniaNistAiRmf,
    canAccessZaniaFairAssessment,
  } = getZaniaTeamMemberRoles(memId);
  // Memoize the fetchCustomAssessmentOptions function to prevent unnecessary recreations
  const fetchCustomAssessmentOptions = useCallback(async () => {
    const updatedHelper = { ...(helper as any) };

    if (type === AGENT_TYPES.GAP_ASSESSMENT) {
      // Only update helper data when it's not loading
      if (!isLoadingFrameworks && !isFetchingFrameworks) {
        try {
          let dropdownOptions = updatedHelper[AGENT_TYPES.GAP_ASSESSMENT].dropdown.options
            .filter((option: any) => {
              if (option.value === 'nist_ai_rmf' && canAccessZaniaNistAiRmf) return true;
              if (option.value === 'nist-csf-2' && canAccessZaniaRiskAssessment) return true;
              if ((option.value === 'soc-2-type-1' || option.value === 'soc-2-type-2') && canAccessZaniaGapAssessment) return true;
              // Add New Framework: only show if user has SOC 2 access AND is admin
              if (option.value === 'add_new_framework' && canAccessZaniaGapAssessment) return true;
              return false;
            });

          const SocOptions = dropdownOptions.slice(0, dropdownOptions.length - 1);
          const AddNewFrameworkOption = dropdownOptions.slice(-1);
          const customList = customFrameworksList?.data ? customFrameworksList.data.map((customAgentDetails: { framework_name: string; id: string; }) => ({
            label: customAgentDetails.framework_name,
            value: customAgentDetails.id
          })) : [];

          updatedHelper[AGENT_TYPES.GAP_ASSESSMENT] = {
            ...updatedHelper[AGENT_TYPES.GAP_ASSESSMENT],
            dropdown: {
              ...updatedHelper[AGENT_TYPES.GAP_ASSESSMENT].dropdown,
              options: [
                ...SocOptions,
                ...customList,
                ...AddNewFrameworkOption
              ],
            },
          };

          setHelperData(updatedHelper[type]);
        } catch (error) {
          console.error('Error fetching frameworks list:', error);
        }
      }
    } else if (type === AGENT_TYPES.RISK_ASSESSMENT) {
      // Filter Risk Assessment dropdown options based on permissions
      let dropdownOptions = updatedHelper[AGENT_TYPES.RISK_ASSESSMENT].dropdown.options
        .filter((option: any) => {
          if(option.value === RiskAssessmentTypes.FAIR && canAccessZaniaFairAssessment) return true;
          return false;
        });

      updatedHelper[AGENT_TYPES.RISK_ASSESSMENT] = {
        ...updatedHelper[AGENT_TYPES.RISK_ASSESSMENT],
        dropdown: {
          ...updatedHelper[AGENT_TYPES.RISK_ASSESSMENT].dropdown,
          options: dropdownOptions,
        },
      };

      setHelperData(updatedHelper[type]);
    } else {
      setHelperData((helper as any)[type as AGENT_TYPES]);
    }
  }, [type, canAccessZaniaNistAiRmf, canAccessZaniaRiskAssessment, canAccessZaniaGapAssessment, canAccessZaniaFairAssessment, customFrameworksList, isLoadingFrameworks, isFetchingFrameworks]);

  // Optimize the useEffect with memoized function
  useEffect(() => {
    fetchCustomAssessmentOptions();
  }, [fetchCustomAssessmentOptions]);

  const [isLoading, setIsLoading] = useState(false);
  const [allFiles, setAllFiles] = useState<UploadedFile[]>([]);
  const [errors, setErrors] = useState<ErrorMessages>({});
  const [selectedDropdownOption, setSelectedDropdownOption] = useState<
    string | string[]
  >();

  // Handle preselection of framework after API call completes
  useEffect(() => {
    // Only handle preselection for Gap Assessment with prefilled framework value
    if (
      data.frameworkValue &&
      type === AGENT_TYPES.GAP_ASSESSMENT &&
      helperData?.dropdown?.options &&
      !selectedDropdownOption
    ) {
      // Check if the framework value exists in the dropdown options
      const frameworkExists = helperData.dropdown.options.some(
        (option: { value: string }) => option.value === data.frameworkValue
      );
      
      if (frameworkExists) {
        setSelectedDropdownOption(data.frameworkValue);
      } else {
        // If framework doesn't exist yet, try refetching the list
        refetchFrameworksList?.();
      }
    }
  }, [data.frameworkValue, data.sessionTitle, type, helperData?.dropdown?.options, selectedDropdownOption, refetchFrameworksList]);

  // Additional effect to handle when the dialog opens with prefilled data
  useEffect(() => {
    // Refetch frameworks list when opening with prefilled framework data
    if (
      isOpen &&
      data.frameworkValue && 
      data.sessionTitle && 
      type === AGENT_TYPES.GAP_ASSESSMENT
    ) {
      // Small delay to ensure the modal is fully opened, then refetch
      const timeoutId = setTimeout(() => {
        refetchFrameworksList?.();
      }, 200);
      
      // Track timeout for cleanup
      timeoutsRef.current.add(timeoutId);
      
      return () => {
        clearTimeout(timeoutId);
        timeoutsRef.current.delete(timeoutId);
      };
    }
  }, [isOpen, data.frameworkValue, data.sessionTitle, type, refetchFrameworksList]);

  // Effect to update helper data when control sets change
  // Moved after hasCustomFrameworkAccess definition

  // Retry mechanism for framework selection
  useEffect(() => {
    if (
      data.frameworkValue && 
      data.sessionTitle && 
      type === AGENT_TYPES.GAP_ASSESSMENT &&
      helperData?.dropdown?.options &&
      !selectedDropdownOption
    ) {
      // Try to select the framework again after a short delay
      // This handles cases where the API response takes time
      const retryTimeoutId = setTimeout(() => {
        const frameworkExists = helperData.dropdown.options.some(
          (option: { value: string }) => option.value === data.frameworkValue
        );
        
        if (frameworkExists && !selectedDropdownOption) {
          setSelectedDropdownOption(data.frameworkValue);
        }
      }, 1000);
      
      // Track timeout for cleanup
      timeoutsRef.current.add(retryTimeoutId);
      
      return () => {
        clearTimeout(retryTimeoutId);
        timeoutsRef.current.delete(retryTimeoutId);
      };
    }
  }, [data.frameworkValue, data.sessionTitle, type, helperData?.dropdown?.options, selectedDropdownOption]);
  const [selectedDropdown2Option, setSelectedDropdown2Option] = useState<
    string
  >();
  const [selectedComplianceFrameworks, setSelectedComplianceFrameworks] = useState<
    string[]
  >([]);
  const [isToggleSelected, setIsToggleSelected] = useState(false);
  const fileUploadRefs = useRef<{ [key: string]: any }>({});
  const inputRefs = useRef<{ [key: string]: any }>({});
  const [inputs, setInputs] = useState<{ [key: string]: string | boolean | string[] }>({});

  // Track scroll fade visibility
  const [scrollFadeClasses, setScrollFadeClasses] = useState<string>("");

  // Track timeouts for cleanup
  const timeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());

  // Track object URLs for cleanup
  const objectUrlsRef = useRef<Set<string>>(new Set());

  // Add cleanup function for when modal closes
  const cleanupUploads = useCallback(() => {
    // Cancel all ongoing uploads in all file upload components
    Object.values(fileUploadRefs.current).forEach((ref) => {
      if (ref && typeof (ref as any).cancelAllUploads === 'function') {
        (ref as any).cancelAllUploads();
      }
    });

    // Clear all timeouts
    timeoutsRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    timeoutsRef.current.clear();

    // Revoke all object URLs to prevent memory leaks
    objectUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        console.warn('Failed to revoke object URL:', error);
      }
    });
    objectUrlsRef.current.clear();
  }, []);

  // Function to scroll to and focus on input field with error
  const scrollToAndFocusInput = useCallback((fieldKey: string) => {
    const timeoutId = setTimeout(() => {
      // First try to find the input in the refs
      let targetElement = inputRefs.current[fieldKey];
      
      if (!targetElement) {
        // If not found in refs, try to find any input with the error state
        const erroredInputs = document.querySelectorAll('[data-invalid="true"], .error, [aria-invalid="true"]');
        for (let i = 0; i < erroredInputs.length; i++) {
          const element = erroredInputs[i];
          // Find the actual input element
          const input = element.tagName === 'INPUT' ? element : element.querySelector('input');
          if (input) {
            targetElement = input;
            break;
          }
        }
      }
      
      if (!targetElement) {
        // Last resort: find any input that might contain the error message
        const errorMessages = document.querySelectorAll('*');
        for (let i = 0; i < errorMessages.length; i++) {
          const element = errorMessages[i];
          if (element.textContent && element.textContent.includes("Session name already exists")) {
            // Find the nearest input element
            let parent = element.parentElement;
            while (parent) {
              const input = parent.querySelector('input');
              if (input) {
                targetElement = input;
                break;
              }
              parent = parent.parentElement;
            }
            break;
          }
        }
      }
      
      if (targetElement) {
        // Find the actual input element if needed
        let actualInput = targetElement;
        if (targetElement.tagName !== 'INPUT' && targetElement.tagName !== 'TEXTAREA') {
          const foundInput = targetElement.querySelector('input, textarea');
          if (foundInput) {
            actualInput = foundInput;
          }
        }
        
        // Scroll into view
        actualInput.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest"
        });
        
        // Focus after scrolling
          actualInput.focus();
      } else {
        console.warn(`Could not find input element for field: ${fieldKey}`);
      }
    }, 200);
    
    // Track timeout for cleanup
    timeoutsRef.current.add(timeoutId);
  }, []);

  // Cleanup when modal closes
  useEffect(() => {

    if (!isOpen) {
      // Small delay to ensure modal close animation doesn't interfere
      const timeoutId = setTimeout(() => {
        cleanupUploads();
        // Reset all state when modal closes
        setAllFiles([]);
        setErrors({});
        setSelectedDropdownOption(undefined);
        setSelectedDropdown2Option(undefined);
        setSelectedComplianceFrameworks([]);
        setIsToggleSelected(false);
        setInputs({});
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, cleanupUploads, type]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupUploads();
    };
  }, [cleanupUploads]);

  // Determine if selectedDropdownOption is a custom framework (for GAP_ASSESSMENT)
  const isCustomGapFrameworkSelected = useMemo(() => {
    if (type !== AGENT_TYPES.GAP_ASSESSMENT || !customFrameworksList?.data || !selectedDropdownOption) return false;
    return (selectedDropdownOption as string) in customFrameworksList.mapping;
  }, [type, customFrameworksList, selectedDropdownOption]);

  // Compliance frameworks options - memoized to prevent recreation
  const complianceFrameworks = useMemo(() => [
    { value: "basel_ii_iii", label: "Basel II/III" },
    { value: "ccpa", label: "CCPA" },
    { value: "cis_controls", label: "CIS Controls" },
    { value: "cobit", label: "COBIT" },
    { value: "coso", label: "COSO" },
    { value: "csa_ccm", label: "CSA CCM" },
    { value: "csa_star", label: "CSA STAR" },
    { value: "enisa", label: "ENISA" },
    { value: "eu_ai_act", label: "EU AI Act" },
    { value: "fair", label: "FAIR" },
    { value: "fisma", label: "FISMA" },
    { value: "fedramp", label: "FedRAMP" },
    { value: "gdpr", label: "GDPR" },
    { value: "glba", label: "GLBA" },
    { value: "hipaa", label: "HIPAA" },
    { value: "hitrust_csf", label: "HITRUST CSF" },
    { value: "isa_iec_62443", label: "ISA/IEC 62443" },
    { value: "iso_iec_27001", label: "ISO/IEC 27001" },
    { value: "iso_iec_27002", label: "ISO/IEC 27002" },
    { value: "iso_iec_27005", label: "ISO/IEC 27005" },
    { value: "iso_iec_27017", label: "ISO/IEC 27017" },
    { value: "iso_iec_27018", label: "ISO/IEC 27018" },
    { value: "iso_iec_27701", label: "ISO/IEC 27701" },
    { value: "iso_iec_38500", label: "ISO/IEC 38500" },
    { value: "iso_iec_42001", label: "ISO/IEC 42001" },
    { value: "itil", label: "ITIL" },
    { value: "nist_ai_rmf", label: "NIST AI RMF" },
    { value: "nist_csf_2", label: "NIST CSF 2.0" },
    { value: "nist_sp_800_171", label: "NIST SP 800-171" },
    { value: "nist_sp_800_30", label: "NIST SP 800-30" },
    { value: "nist_sp_800_37", label: "NIST SP 800-37" },
    { value: "nist_sp_800_53", label: "NIST SP 800-53" },
    { value: "oecd_ai_principles", label: "OECD AI Principles" },
    { value: "owasp_asvs", label: "OWASP ASVS" },
    { value: "owasp_samm", label: "OWASP SAMM" },
    { value: "owasp_top_10", label: "OWASP Top 10" },
    { value: "pci_dss", label: "PCI DSS" },
    { value: "soc_1", label: "SOC 1" },
    { value: "soc_2_type_1", label: "SOC 2 Type I" },
    { value: "soc_2_type_2", label: "SOC 2 Type II" },
    { value: "sox", label: "SOX" },
    { value: "swift_csp", label: "SWIFT CSP" }
  ], []);

  // Dynamic labels and placeholders based on documentation type (memoized)
  const getDynamicLabelAndPlaceholder = useCallback((docType: string) => {
    const typeMap: { [key: string]: { label: string; placeholder: string } } = {
      'Policy': { label: 'Policy Name', placeholder: 'Add policy name' },
      'Standard': { label: 'Standard Name', placeholder: 'Add standard name' },
      'Guideline': { label: 'Guideline Name', placeholder: 'Add guideline name' },
      'Procedure': { label: 'Procedure Name', placeholder: 'Add procedure name' }
    };
    return typeMap[docType] || { label: 'Policy Name', placeholder: 'Add policy name' };
  }, []);

  // Get the logged-in member to check permissions (memoized)
  const member = useLoggedInMember();
  
  // Memoize user roles calculation to prevent unnecessary recomputation
  const userRoles = useMemo(() => 
    member?.roles?.map(({ role_id }) => role_id as TeamRole) || [], 
    [member?.roles]
  );
  
  // Check if user has access to FAIR assessment (memoized)
  const hasFairAccess = useMemo(() => 
    userRoles.includes(TeamRole.ZANIA_FAIR_ASSESSMENT), 
    [userRoles]
  );

  // Check if user is admin for custom framework access (memoized)
  const memberId = member?.member_id || "";
  const { isTeamOrgAdmin, isTeamAdmin } = useMemo(() =>
    member ? getZaniaTeamMemberRoles(memberId, member) : { isTeamOrgAdmin: false, isTeamAdmin: false },
    [member, memberId]
  );

  const hasCustomFrameworkAccess = useMemo(() =>
    isTeamOrgAdmin || isTeamAdmin,
    [isTeamOrgAdmin, isTeamAdmin]
  );

  // Filter dropdown options based on permissions

  // Update the filteredDropdownOptions useMemo to handle the custom option
const filteredDropdownOptions = useMemo(() => {
  if (!helperData?.dropdown?.options) return [];

  return (helperData.dropdown.options as { value: string; label: string; isDefault: boolean; isCustom?: boolean }[]).filter(option => {
    // Always show the currently selected option, even if it would otherwise be filtered out
    if (option.value === selectedDropdownOption) {
      return true;
    }

    // If it's the FAIR option, only show it if the user has access
    if (option.value === RiskAssessmentTypes.FAIR) {
      return hasFairAccess;
    }

    // If it's a custom option (Add New Framework), only show it if user is admin/owner
    if (option.isCustom) {
      if (type === AGENT_TYPES.GAP_ASSESSMENT) {
        // For Gap Assessment, only admins and org admins can see "Add New Framework"
        return hasCustomFrameworkAccess;
      }
      // For any other types with custom options, default to admin access
      return hasCustomFrameworkAccess;
    }

    // Show all other options
    return true;
  });
}, [helperData?.dropdown?.options, hasFairAccess, hasCustomFrameworkAccess, type, selectedDropdownOption]);

  // Check if SOC 2 Type I or Type II is selected in Gap Assessment
  const isSocFrameworkSelected = useMemo(() => {
    if (type !== AGENT_TYPES.GAP_ASSESSMENT) return false;
    return selectedDropdownOption === GapAssessmentTypes.SOC2_TYPE1 || 
           selectedDropdownOption === GapAssessmentTypes.SOC2_TYPE2;
  }, [type, selectedDropdownOption]);

  // Set default framework option
  useEffect(() => {
    let defaultOption = helperData?.dropdown?.options.find(
      (option: { isDefault: boolean }) => option.isDefault
    );
    if (!defaultOption && helperData?.dropdown?.options?.[0]) {
      defaultOption = helperData.dropdown.options[0];
    }

    // Auto-select default option when modal opens for ALL agent types EXCEPT Gap Assessment
    if (isOpen && defaultOption?.value && !selectedDropdownOption && type !== AGENT_TYPES.GAP_ASSESSMENT) {
      setSelectedDropdownOption(defaultOption.value);
    }
    
    // Set default option for dropdown2 if present
    if (helperData?.dropdown2) {
      let defaultOption2 = helperData.dropdown2.options.find(
        (option: { isDefault: boolean }) => option.isDefault
      );
      if (!defaultOption2 && helperData.dropdown2.options[0]) {
        defaultOption2 = helperData.dropdown2.options[0];
      }
      setSelectedDropdown2Option(defaultOption2?.value ?? "");
    }
  }, [helperData?.dropdown?.options, helperData?.dropdown2?.options, type, isOpen]);

  // Use effect to set contact email from logged in user when requestVendor is toggled on
  useEffect(() => {
    if (inputs.requestVendor === true && member?.email_address && (!inputs.contactEmail || inputs.contactEmail === '')) {
      setInputs(prevInputs => ({
        ...prevInputs,
        contactEmail: member.email_address
      }));
    }
  }, [inputs.requestVendor, member]);


  // Combining the framework change and modal open effects into one
  useEffect(() => {
    if (isOpen) {
      // When modal opens, reset everything except session title
      setIsLoading(false);
      setErrors({});
      setSelectedComplianceFrameworks([]); // Reset compliance frameworks
      setAllFiles([]); // Reset files
      
      // For GAP_ASSESSMENT, we need to initialize the title based on the data.sessionTitle
      if (type === AGENT_TYPES.GAP_ASSESSMENT && data.sessionTitle) {
        setInputs(prevInputs => ({
          ...prevInputs,
          title: data.sessionTitle || ""
        }));
      }
      // Only reset dropdown selection for GAP_ASSESSMENT or when there's prefilled data that needs special handling
      if (type === AGENT_TYPES.GAP_ASSESSMENT || (data.sessionTitle && data.frameworkValue)) {
        setSelectedDropdownOption(undefined); // Reset dropdown selection
      }
      setIsToggleSelected(false); // Reset toggle
      setInputs(prevInputs => {
        const newInputs = { ...prevInputs };
        
        // Handle prefilled data from framework creation
        if (data.sessionTitle && data.frameworkValue) {
          // Don't set framework selection here - wait for API call to complete
          // Return with title prefilled
          return { 
            ...newInputs,
            title: data.sessionTitle,
            contactEmail: member?.email_address || ''
          };
        } else {
          // Only preserve the session title for normal operation
          const sessionTitle = newInputs.session;
          
          return { 
            ...newInputs,
            session: typeof sessionTitle === 'string' ? sessionTitle : '',
            contactEmail: member?.email_address || '' 
          };
        }
      });
    }
  }, [isOpen, member, type, data]); // Add 'data' to dependencies

  // Separate effect for framework changes
  useEffect(() => {
    if (selectedDropdownOption) {
      setIsToggleSelected(false);
      setAllFiles([]);
      // Reset all inputs except session title
      setInputs(prevInputs => ({
        ...prevInputs,
        risk_statement: '', // Clear risk statement when framework changes
      }));
    }
  }, [selectedDropdownOption]);

  // Add this useEffect to set the default radio value when the dropdown changes
  useEffect(() => {
    if (selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 && helperData?.radio) {
      // Find the default option or use the first option if no default is specified
      const defaultRadioOption = helperData.radio.options.find(
        (opt: { value: string; label: string; isDefault?: boolean }) => opt.isDefault
      )?.value || helperData.radio.options[0]?.value;

      setInputs(prevInputs => ({
        ...prevInputs,
        [helperData.radio.key]: defaultRadioOption
      }));
    }
  }, [selectedDropdownOption, helperData]);

  // Add this useEffect to set the default radio value when the dropdown changes for Gap Assessment
  useEffect(() => {
    // Only set radio defaults for NIST CSF 2 in Gap Assessment (not AI RMF)
    if (selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 && 
        type === AGENT_TYPES.GAP_ASSESSMENT && 
        helperData?.radio) {
      // Find the default option or use the first option if no default is specified
      const defaultRadioOption = helperData.radio.options.find(
        (opt: { value: string; label: string; isDefault?: boolean }) => opt.isDefault
      )?.value || helperData.radio.options[0]?.value;

      setInputs(prevInputs => ({
        ...prevInputs,
        [helperData.radio.key]: defaultRadioOption
      }));
    }
  }, [selectedDropdownOption, helperData, type]);

  // Scroll detection for fade indicators
  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;

      // Threshold for showing/hiding fades (5px buffer)
      const atTop = scrollTop <= 5;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 5;

      let classes = "scroll-fade-container";
      if (!atTop) classes += " show-top-fade";
      if (atBottom) classes += " hide-bottom-fade";

      setScrollFadeClasses(classes);
    };

    // Find the listbox wrapper element when dropdown opens
    const findAndAttachListener = () => {
      const listboxWrapper = document.querySelector('.custom-dropdown-scrollbar[role="listbox"]');
      if (listboxWrapper) {
        listboxWrapper.addEventListener('scroll', handleScroll as EventListener);
        // Initial check
        handleScroll({ target: listboxWrapper } as any);
        return listboxWrapper;
      }
      return null;
    };

    // Try to attach listener with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const listbox = findAndAttachListener();
      if (listbox) {
        timeoutsRef.current.add(timeoutId);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      const listboxWrapper = document.querySelector('.custom-dropdown-scrollbar[role="listbox"]');
      if (listboxWrapper) {
        listboxWrapper.removeEventListener('scroll', handleScroll as EventListener);
      }
    };
  }, [isOpen, selectedDropdownOption, type]);

  const isCustomizeNistCsf2 =
      selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 &&
      inputs[helperData?.radio?.key] === "customize";

  const isCustomizeNistInGapAssessment =
      type === AGENT_TYPES.GAP_ASSESSMENT &&
      selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 &&
      inputs[helperData?.radio?.key] === "customize";

  // Check if NIST frameworks are selected in Gap Assessment (for file uploads)
  const isNistFrameworkForUploads = useMemo(() => {
    if (type !== AGENT_TYPES.GAP_ASSESSMENT) return false;
    // For NIST CSF 2: only show uploads if "all" option is selected
    // For NIST AI RMF: always show uploads (no radio buttons)
    return (selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 && inputs[helperData?.radio?.key] === "all") ||
           (selectedDropdownOption === "nist_ai_rmf");
  }, [type, selectedDropdownOption, inputs, helperData]);

  // Memoize email validation function
  const isValidEmail = useCallback((email: string) => {
    return EMAIL_REGEX.test(email);
  }, []);

  // Memoize validation helpers to prevent recreating on every render
  const validationHelpers = useMemo(() => ({
    hasValidationErrors: (errors: ErrorMessages) => 
      Object.values(errors).some(error => error !== "" && error !== undefined),
    
    areRequiredInputsMissing: (helperData: any, inputs: any) =>
      helperData?.inputs?.some(
        (input: { key: string; isRequired: boolean }) => {
          if (!input.isRequired) return false;

          const value = inputs[input.key] ?? "";

          // Handle vendorName specially since it can be an object
          if (input.key === "vendorName") {
            if (typeof value === "object" && value !== null) {
              return !value.name || value.name.trim().length === 0;
            }
            return (value as string).trim().length === 0;
          }

          // Handle other fields as strings
          return (value as string).trim().length === 0;
        }
      ),
    
    isComplianceFrameworkMissing: (type: string | undefined, selectedComplianceFrameworks: string[]) => 
      type === AGENT_TYPES.POLICY_MANAGEMENT && (!selectedComplianceFrameworks || selectedComplianceFrameworks.length === 0),
    
    isPolicyUploadMissing: (type: string | undefined, selectedDropdown2Option: string | undefined, allFiles: UploadedFile[]) => 
      type === AGENT_TYPES.POLICY_MANAGEMENT && selectedDropdown2Option === "update_existing" && 
      allFiles.filter(file => file.type === "policy").length === 0,
    
    isRiskStatementMissing: (selectedDropdownOption: string | undefined, inputs: any) =>
      selectedDropdownOption === RiskAssessmentTypes.FAIR &&
      (!inputs.risk_statement || (inputs.risk_statement as string).trim().length === 0),

    isFrameworkSelectionMissing: (selectedDropdownOption: string | string[] | undefined, type: string | undefined) =>
      (!selectedDropdownOption || (Array.isArray(selectedDropdownOption) ? selectedDropdownOption.length === 0 : !selectedDropdownOption)) && (type === AGENT_TYPES.GAP_ASSESSMENT || type === AGENT_TYPES.RISK_ASSESSMENT),
    
    hasFileUploadIssues: (allFiles: UploadedFile[]) => {
      // Filter out any files that might be in a transitional state
      const activeFiles = allFiles.filter(file => file.file && file.type);
      const hasIssues = activeFiles.some((file) => file.status !== "success");

      return hasIssues;
    }
  }), []);

  const buttonDisabled = useMemo(() => {
    const {
      hasValidationErrors,
      areRequiredInputsMissing,
      isComplianceFrameworkMissing,
      isPolicyUploadMissing,
      isRiskStatementMissing,
      isFrameworkSelectionMissing,
      hasFileUploadIssues
    } = validationHelpers;

    // Check for validation errors
    if (hasValidationErrors(errors)) return true;

    // Check required inputs
    if (areRequiredInputsMissing(helperData, inputs)) return true;

    // Policy management validations
    if (isComplianceFrameworkMissing(type, selectedComplianceFrameworks)) return true;
    if (isPolicyUploadMissing(type, selectedDropdown2Option, allFiles)) return true;

    // FAIR risk statement validation
    if (isRiskStatementMissing(selectedDropdownOption as string, inputs)) return true;

    // Framework selection validation
    if (isFrameworkSelectionMissing(selectedDropdownOption, type)) return true;

    // Basic type validation
    if (!type) return true;

    // File upload validation (optimized)
    if (type === AGENT_TYPES.GAP_ASSESSMENT) {
      if(!inputs.title){
        return true;
      }
      if (isNistFrameworkForUploads) {
        const uploads = helperData?.uploads ?? [];
        for (const upload of uploads) {
          const filteredFiles = allFiles.filter(file => file.type === upload.type);
          const allowedMaxFiles = selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 ? 400 : upload.maxFiles;
          
          if (upload.isRequired && !filteredFiles.length && !isCustomizeNistInGapAssessment) return true;
          if (filteredFiles.length > allowedMaxFiles) return true;
        }
      }
    } else {
      const uploads = helperData?.uploads ?? [];
      for (const upload of uploads) {
        const filteredFiles = allFiles.filter(file => file.type === upload.type && file.file && file.status);
        const allowedMaxFiles = selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 ? 400 : upload.maxFiles;
        
        // Special handling for questionnaire - both main and kb uploads are required
        if (type === AGENT_TYPES.QUESTIONNAIRE) {
          if (upload.isRequired && !filteredFiles.length) return true;
          if (filteredFiles.length > allowedMaxFiles) return true;
        } else {
          if (upload.isRequired && !filteredFiles.length && 
              selectedDropdownOption !== RiskAssessmentTypes.FAIR && !isCustomizeNistCsf2) return true;
          if (filteredFiles.length > allowedMaxFiles) return true;
        }
      }
    }

    // File status validation
    return hasFileUploadIssues(allFiles);
  }, [
    validationHelpers, 
    errors, 
    helperData, 
    inputs, 
    isValidEmail, 
    type, 
    selectedComplianceFrameworks, 
    selectedDropdown2Option, 
    allFiles, 
    selectedDropdownOption, 
    isNistFrameworkForUploads, 
    isCustomizeNistInGapAssessment, 
    isCustomizeNistCsf2
  ]);



  const handleSubmit = async () => {
    setIsLoading(true);
    // Create a trimmed copy of inputs for validation and submission.
    // Do not mutate the UI state `inputs` directly here; trimming is applied only to the submitted payload.
    const trimmedInputs: Record<string, string | boolean | string[]> = Object.keys(inputs).reduce(
      (acc: Record<string, any>, key) => {
        const val = inputs[key];
        if (typeof val === "string") acc[key] = val.trim();
        else if (Array.isArray(val)) acc[key] = val.map((v) => (typeof v === "string" ? v.trim() : v));
        else acc[key] = val;
        return acc;
      },
      {}
    );

    let isError = false;

    // Function to validate a single input field
    const validateInputField = (input: any) => {
      if (input?.isRequired && !inputs[input.key]) {
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: "This field is required",
        }));
      }
    }
    for (const input of helperData?.inputs ?? []) {
      if (input?.isRequired && !trimmedInputs[input.key]) {
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: "This field is required",
        }));
      }

      // character limit for input - use dynamic limit based on agent type
      const characterLimit = getCharacterLimit(type, input.key);
      if (trimmedInputs[input.key] && input.type === "text" && trimmedInputs[input.key].toString().length > characterLimit) {
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: `Input cannot exceed ${characterLimit} characters`,
        }));
      }

      // Updated validation logic in handleSubmit
      if (input.key === "title") {
        if (startsWithSpecialCharacter(trimmedInputs[input.key] as string)) {
          isError = true;
          setErrors((prevErrors) => ({
            ...prevErrors,
            [input.key]: "Input cannot start with a special character",
          }));
        } else if (containsProblematicCharacters(trimmedInputs[input.key] as string)) {
          isError = true;
          setErrors((prevErrors) => ({
            ...prevErrors,
            [input.key]: "Input cannot contain emojis or problematic symbols",
          }));
        }
      } else if (input.key === "vendorName") {
        const vendorNameValue = typeof trimmedInputs[input.key] === "object"
          ? (trimmedInputs[input.key] as any)?.name
          : trimmedInputs[input.key] as string;
        if (vendorNameValue && startsWithSpecialCharacter(vendorNameValue)) {
          isError = true;
          setErrors((prevErrors) => ({
            ...prevErrors,
            [input.key]: "Special characters are not allowed.",
          }));
        }
      } else if (trimmedInputs[input.key] && typeof trimmedInputs[input.key] === "string" && containsSpecialCharacters(trimmedInputs[input.key] as string)) {
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: "Special characters are not allowed.",
        }));
      }
    };
    
    // Validate regular inputs
    for (const input of helperData?.inputs ?? []) {
      validateInputField(input);
    }
    
    // Validate inputsAfterDropdown for GAP_ASSESSMENT
    if (type === AGENT_TYPES.GAP_ASSESSMENT && helperData?.inputsAfterDropdown) {
      for (const input of helperData.inputsAfterDropdown) {
        validateInputField(input);
      }
    }

    // Skip vendor email and contact email validation when requestVendor is false
    if ((trimmedInputs as any).requestVendor !== true) {
      // Clear any existing errors for vendor-related fields
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.vendorEmail;
        delete newErrors.contactEmail;
        return newErrors;
      });
    }

    if (isError) {
      setIsLoading(false);
      return;
    }

    try {
      // Call processDataBasedOnAssessment and capture any returned session ID
      const sessionId = await processDataBasedOnAssessment(
        type,
        allFiles,
        navigate,
        // pass trimmed inputs to the processor
        trimmedInputs,
        selectedDropdownOption as string,
        isToggleSelected,
        undefined, // reassessOption
        undefined, // agentData
        undefined, // prevSourceFiles
      );

    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      
      // Handle validation errors specially (show inline error)
      if(isValidationError(axiosError) && (axiosError.response?.data as any)?.error_message.includes("name already exists")){
          setErrors({ title: "Session name already exists" });
          // Scroll to and focus on the title/session input field
          scrollToAndFocusInput("title");
        }
      
      // Log detailed error for debugging
      console.error('API Error:', axiosError);
      
      // Get user-friendly error message
      const errorMessage = getApiErrorMessage(axiosError);
      
      // Show error notification
      addNotification({
        message: errorMessage,
        type: "error",
      });
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  const handleSetFiles = useCallback((newFiles: UploadedFile[], fileType: string) => {
    setAllFiles((prevFiles) => {
      // Revoke object URLs for files being removed
      const otherTypeFiles = prevFiles.filter((file) => file.type !== fileType);
      const removedFiles = prevFiles.filter((file) => file.type === fileType);
      
      removedFiles.forEach((file) => {
        // Track any object URLs that were created and revoke them
        if (file.file && typeof file.file === 'object' && 'name' in file.file) {
          // Check if this file has any object URLs we need to revoke
          const fileObjectUrl = (file as any).objectUrl;
          if (fileObjectUrl && objectUrlsRef.current.has(fileObjectUrl)) {
            try {
              URL.revokeObjectURL(fileObjectUrl);
              objectUrlsRef.current.delete(fileObjectUrl);
            } catch (error) {
              console.warn('Failed to revoke object URL:', error);
            }
          }
        }
      });

      // Track new object URLs
      newFiles.forEach((file) => {
        const fileObjectUrl = (file as any).objectUrl;
        if (fileObjectUrl) {
          objectUrlsRef.current.add(fileObjectUrl);
        }
      });

      const combinedFiles = [...otherTypeFiles, ...newFiles];
      const uniqueFiles = combinedFiles.reduce((acc, current) => {
        const existingFileIndex = acc.findIndex(
          (item) => item.file.name === current.file.name && item.type === current.type
        );
        
        if (existingFileIndex === -1) {
          return [...acc, current];
        } else {
          // Revoke old file's object URL if it exists
          const existingFile = acc[existingFileIndex];
          const oldFileObjectUrl = (existingFile as any).objectUrl;
          if (oldFileObjectUrl && objectUrlsRef.current.has(oldFileObjectUrl)) {
            try {
              URL.revokeObjectURL(oldFileObjectUrl);
              objectUrlsRef.current.delete(oldFileObjectUrl);
            } catch (error) {
              console.warn('Failed to revoke old object URL:', error);
            }
          }
          
          // Replace the existing file with the updated one
          const newAcc = [...acc];
          newAcc[existingFileIndex] = current;
          return newAcc;
        }
      }, [] as UploadedFile[]);

      // Scroll to the newly added file
      const scrollTimeoutId = setTimeout(() => {
        const lastFile = newFiles[newFiles.length - 1];
        if (lastFile && fileUploadRefs.current[fileType]) {
          fileUploadRefs.current?.[fileType]?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }, 100);
      
      // Track timeout for cleanup
      timeoutsRef.current.add(scrollTimeoutId);

      return uniqueFiles;
    });
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
    type: string
  ) => {
    const newValue = e.target.value.trimStart();

    // Clear existing error for this field
    setErrors({ ...errors, [key]: "" });

    // Skip validation if field is empty (except for required fields, which are validated on submit)
    if (!newValue) {
      // Update inputs state with empty value
      setInputs({
        ...inputs,
        [key]: newValue,
      });
      return;
    }

    // Validate input based on field type and key
    let validationError = "";

    // Skip email validation during typing - it will be handled on blur
    // Email validation for vendorEmail and contactEmail fields
    // Character limit validation - use dynamic limit based on agent type
    if (type === 'text' && key !== "vendorEmail" && key !== "contactEmail") {
      const characterLimit = getCharacterLimit(data.type, key);
      if (newValue.length > characterLimit) {
        validationError = `Input cannot exceed ${characterLimit} characters`;
      }
    }
    // Updated validation logic for different field types
    else if (key === "title") {
      if (startsWithSpecialCharacter(newValue)) {
        validationError = "Input cannot start with a special character";
      } else if (containsProblematicCharacters(newValue)) {
        validationError = "Input cannot contain emojis or problematic symbols";
      }
    }
    else if (key === "vendorName") {
      const vendorNameValue = typeof newValue === "object" ? (newValue as any)?.name : newValue;
      if (vendorNameValue && startsWithSpecialCharacter(vendorNameValue)) {
        validationError = "Input cannot start with a special character";
      }
    }
    else if (newValue && key !== "vendorEmail" && key !== "contactEmail") {
      // For all other text fields (except emails), check for starting special char and problematic characters
      if (startsWithSpecialCharacter(newValue)) {
        validationError = "Input cannot start with a special character";
      }
    }

    // Update error state if validation failed
    if (validationError) {
      setErrors(prev => ({ ...prev, [key]: validationError }));
    }

    // Update inputs state regardless of validation result
    setInputs({
      ...inputs,
      [key]: newValue,
    });
  };


  // Safety check to make sure helperData exists
  if (!helperData && type) {
    console.warn("helperData is missing, attempting to reset from helper");
    const defaultHelperData = (helper as any)[type as AGENT_TYPES];
    if (defaultHelperData) {
      setHelperData(defaultHelperData);
    }
  }

  return (
    <ZModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          // Cancel uploads before closing
          cleanupUploads();
        }
        if(!isLoading){
          setIsOpen({
            isOpen: false,
            type: undefined
          })
        }
      }}
      aria-label="Upload Dialog"
      className="max-h-[calc(100vh-96px)] overflow-hidden"
    >
      <ZModalContent className="max-w-lg !my-0">
        <ZModalHeader className="flex text-lg flex-col w-full items-center pb-4">
          <p>{helperData?.title || "Loading..."}</p>
          <p className="text-sm text-center font-normal text-[#777780]">
            {helperData?.desc || "Please wait..."}
          </p>
        </ZModalHeader>
        <ZModalBody className="pt-0 overflow-auto">
          <div className={`grid py-0 ${type === AGENT_TYPES.GAP_ASSESSMENT ? '' : 'gap-4'}`}>
            <div className="grid w-full items-center gap-4">
              {/* Display Session Title first */}
              {type === AGENT_TYPES.POLICY_MANAGEMENT && helperData?.inputs?.[0] ? (
                <ZInput
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[helperData.inputs[0].key] = el;
                    }
                  }}
                  key={helperData.inputs[0].key}
                  label={helperData.inputs[0].label}
                  placeholder={helperData.inputs[0].placeholder}
                  type={helperData.inputs[0].type}
                  value={
                    inputs[helperData.inputs[0].key] &&
                      typeof inputs[helperData.inputs[0].key] === "string"
                      ? (inputs[helperData.inputs[0].key] as string)
                      : ""
                  }
                  onChange={(e) => handleInputChange(e, helperData.inputs[0].key, helperData.inputs[0].type)}
                  labelPlacement="outside"
                  maxLength={25}
                  errorMessage={errors[helperData.inputs[0].key]}
                  isInvalid={!!errors[helperData.inputs[0].key]}
                  variant="faded"
                  autoFocus={true}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors[helperData.inputs[0].key] && "border-[#F31260]"
                    ),
                  }}
                />
              ) : null}
              
              {/* Side-by-side dropdowns for policy management */}
              {type === AGENT_TYPES.POLICY_MANAGEMENT && helperData?.dropdown && helperData?.dropdown2 ? (
                <div className="flex gap-4 w-full">
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <span className="text-sm">{helperData.dropdown.label}</span>
                      <Tooltip 
                        showArrow 
                        placement="bottom"
                        offset={10}
                        classNames={{
                          content: "p-3 w-[240px] bg-black opacity-90 text-white rounded-md",
                          arrow: "bg-black",
                          base: "z-[9999]"
                        }}
                        content={
                          <div className="text-sm">
                            <p className="text-xs mb-8">What type of documentation are you working with?</p>
                            
                            <ul className="list-disc pl-5 space-y-2 text-xs">
                              <li >Policy (High-level intent)</li>
                              <li>Standard (Specific requirements to meet control objectives)</li>
                              <li>Guideline (Recommended but non-mandatory guidance)</li>
                              <li>Procedure (Step-by-step implementation instructions)</li>
                            </ul>
                          </div>
                        }
                      >
                        <span><InfoIcon className="h-4 w-4 ml-1 text-gray-500 cursor-help" /></span>
                      </Tooltip>
                    </div>
                    <Select
                      classNames={{
                        trigger: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors.session && "border-[#F31260]"
                        ),
                        popoverContent: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors.session && "border-[#F31260]"
                        ),
                      }}
                      items={helperData.dropdown.options}
                      labelPlacement="outside"
                      value={selectedDropdownOption as string}
                      placeholder={helperData.dropdown.placeholder}
                      onChange={(value) => {
                        setSelectedDropdownOption(value.target.value);
                      }}
                      selectedKeys={selectedDropdownOption ? (Array.isArray(selectedDropdownOption) ? new Set(selectedDropdownOption) : new Set([selectedDropdownOption])) : new Set()}
                      disabledKeys={selectedDropdownOption ? (Array.isArray(selectedDropdownOption) ? new Set(selectedDropdownOption) : new Set([selectedDropdownOption])) : new Set()}
                    >
                      {(item: { value: string; label: string }) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      )}
                    </Select>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <span className="text-sm">{helperData.dropdown2.label}</span>
                      <Tooltip 
                        showArrow 
                        placement="bottom"
                        offset={10}
                        classNames={{
                          content: "p-3 w-[240px] bg-black opacity-90 text-white rounded-md",
                          arrow: "bg-black",
                          base: "z-[9999]"
                        }}
                        content={
                          <div className="text-xs">
                            <p className="mb-6">What do you want to do with the selected documentation type?</p>
                            <ul className="list-disc pl-5 space-y-2 text-xs">
                              <li>Create from scratch</li>
                              <li>Update an existing item</li>
                              <li>Derive from another type <span className="text-xs ">(e.g., derive a procedure from policy)</span></li>
                            </ul>
                          </div>
                        }
                      >
                        <span><InfoIcon className="h-4 w-4 ml-1 text-gray-500 cursor-help" /></span>
                      </Tooltip>
                    </div>
                    <Select
                      classNames={{
                        trigger: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors.session && "border-[#F31260]"
                        ),
                        popoverContent: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors.session && "border-[#F31260]"
                        ),
                      }}
                      labelPlacement="outside"
                      value={selectedDropdown2Option}
                      placeholder={helperData.dropdown2.placeholder}
                      onChange={(value) => {
                        setSelectedDropdown2Option(value.target.value);
                        setInputs({
                          ...inputs,
                          [helperData.dropdown2.key]: value.target.value,
                        });
                        // Clear ALL files when switching between actions
                        setAllFiles([]);
                      }}
                      selectedKeys={selectedDropdown2Option ? new Set([selectedDropdown2Option]) : new Set()}
                      disabledKeys={selectedDropdown2Option ? new Set([selectedDropdown2Option]) : new Set()}
                    >
                      {helperData.dropdown2.options.map((item: { value: string; label: string }) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>
              ) : null}
              
              {/* Display Policy Name and Context fields */}
              {type === AGENT_TYPES.POLICY_MANAGEMENT && helperData?.inputs ? 
                helperData.inputs.slice(1).map((input: {
                  key: string;
                  label: string;
                  placeholder: string;
                  type: string;
                  isRequired: boolean;
                }, index: number) => {
                  // Show Upload Document field when "Update" is selected
                  if (input.key === "policyName" && selectedDropdown2Option === "update_existing") {
                    const dynamicContent = getDynamicLabelAndPlaceholder((selectedDropdownOption as string) || 'policy');
                    return (
                      <div key={input.key}>
                        {/* Policy Name Text Input - Show first */}
                        <ZInput
                          ref={(el) => {
                            if (el) {
                              inputRefs.current[input.key] = el;
                            }
                          }}
                          label={dynamicContent.label}
                          placeholder={dynamicContent.placeholder}
                          type={input.type}
                          value={
                            inputs[input.key] &&
                              typeof inputs[input.key] === "string"
                              ? (inputs[input.key] as string)
                              : ""
                          }
                          onChange={(e) => handleInputChange(e, input.key, input.type)}
                          labelPlacement="outside"
                          maxLength={25}
                          errorMessage={errors[input.key]}
                          isInvalid={!!errors[input.key]}
                          variant="faded"
                          className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                          classNames={{
                            inputWrapper: cn(
                              "w-full shadow-none rounded-md border-1 border-gray-300",
                              !!errors[input.key] && "border-[#F31260]"
                            ),
                          }}
                        />

                        {/* Upload Document to Update - Show second */}
                        <div className="grid w-full items-center gap-1.5 mt-4">
                          <p className="text-sm">Upload Document to Update</p>
                          <FileUpload
                            key={`policy-${selectedDropdown2Option}`}
                            maxFiles={1}
                            isMultiple={false}
                            allowedFileTypes={[
                              "application/pdf",
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                              "application/msword"
                            ]}
                            setFiles={(files: UploadedFile[], type: string) =>
                              handleSetFiles(files, "policy")
                            }
                            type="policy"
                            ref={(el: HTMLDivElement) => {
                              fileUploadRefs.current.policy = el;
                            }}
                          />
                        </div>
                        
                        {/* Compliance Frameworks Dropdown - Always show */}
                        <div className="grid w-full items-center gap-1.5 mt-4">
                          <p className="text-sm">Compliance Frameworks</p>
                          <Select
                            classNames={{
                              trigger: cn(
                                "w-full shadow-none rounded-md border-1 border-gray-300"
                              ),
                              popoverContent: cn(
                                "w-full shadow-none rounded-md border-1 border-gray-300"
                              ),
                            }}
                            labelPlacement="outside"
                            placeholder="Select compliance frameworks"
                            selectionMode="multiple"
                            onSelectionChange={(keys) => {
                              const selectedArray = Array.from(keys as Set<string>);
                              setSelectedComplianceFrameworks(selectedArray);
                              setInputs({
                                ...inputs,
                                complianceFrameworks: selectedArray,
                              });
                            }}
                            selectedKeys={new Set(selectedComplianceFrameworks)}
                            renderValue={(items) => {
                              if (items.length === 0) return "";
                              if (items.length === 1) return items[0].textValue;
                              
                              // Create a temporary element to measure text width
                              const measureText = (text: string) => {
                                const canvas = document.createElement('canvas');
                                const context = canvas.getContext('2d');
                                if (context) {
                                  context.font = '14px system-ui, -apple-system, sans-serif';
                                  return context.measureText(text).width;
                                }
                                return text.length * 8; // Fallback estimate
                              };
                              
                              const maxWidth = 280; // Approximate available width in the select
                              
                              // If all items fit, return them all
                              const allText = items.map(item => item.textValue).join(", ");
                              if (measureText(allText) <= maxWidth) {
                                return allText;
                              }
                              
                              // Otherwise, find how many we can fit with "+ X others"
                              let displayText = items[0].textValue;
                              let displayCount = 1;
                              
                              // Try to fit as many frameworks as possible while leaving room for "+ X others"
                              for (let i = 1; i < items.length - 1; i++) {
                                const remaining = items.length - (i + 1);
                                const testText = displayText + ", " + items[i].textValue;
                                const withOthers = `${testText} + ${remaining} others`;
                                
                                if (measureText(withOthers) <= maxWidth) {
                                  displayText = testText;
                                  displayCount++;
                                } else {
                                  break;
                                }
                              }
                              
                              const remaining = items.length - displayCount;
                              return (
                                <span>
                                  {displayText} <span style={{ color: '#006FEE' }}>+ {remaining} others</span>
                                </span>
                              );
                            }}
                          >
                            {complianceFrameworks.map((framework) => (
                              <SelectItem key={framework.value} value={framework.value}>
                                {framework.label}
                              </SelectItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                    );
                  }
                  
                  // For Policy Name field when NOT updating, add the new components after it
                  if (input.key === "policyName" && selectedDropdown2Option !== "update_existing") {
                    const dynamicContent = getDynamicLabelAndPlaceholder((selectedDropdownOption as string) || 'policy');
                    return (
                      <div key={input.key}>
                        {/* Dynamic Name Input based on Documentation Type */}
                        <ZInput
                          ref={(el) => {
                            if (el) {
                              inputRefs.current[input.key] = el;
                            }
                          }}
                          label={dynamicContent.label}
                          placeholder={dynamicContent.placeholder}
                          type={input.type}
                          value={
                            inputs[input.key] &&
                              typeof inputs[input.key] === "string"
                              ? (inputs[input.key] as string)
                              : ""
                          }
                          onChange={(e) => handleInputChange(e, input.key, input.type)}
                          labelPlacement="outside"
                          maxLength={25}
                          errorMessage={errors[input.key]}
                          isInvalid={!!errors[input.key]}
                          variant="faded"
                          className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                          classNames={{
                            inputWrapper: cn(
                              "w-full shadow-none rounded-md border-1 border-gray-300",
                              !!errors[input.key] && "border-[#F31260]"
                            ),
                          }}
                        />
                        
                        {/* Derive from Document - File Upload (Optional) - Only for Create/Derive */}
                        <div className="grid w-full items-center gap-1.5 mt-4">
                          <p className="text-sm">Derive from Documents <span className="">(Optional)</span></p>
                          <FileUpload
                            key={`derive-document-${selectedDropdown2Option}`}
                            maxFiles={5}
                            isMultiple={true}
                            allowedFileTypes={[
                              "application/pdf",
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                              "application/msword"
                            ]}
                            setFiles={(files: UploadedFile[], type: string) =>
                              handleSetFiles(files, "derive-document")
                            }
                            type="derive-document"
                            ref={(el: HTMLDivElement) => {
                              fileUploadRefs.current["derive-document"] = el;
                            }}
                          />
                        </div>
                        
                        {/* Compliance Frameworks Dropdown */}
                        <div className="grid w-full items-center gap-1.5 mt-4">
                          <p className="text-sm">Compliance Frameworks</p>
                          <Select
                            classNames={{
                              trigger: cn(
                                "w-full shadow-none rounded-md border-1 border-gray-300"
                              ),
                              popoverContent: cn(
                                "w-full shadow-none rounded-md border-1 border-gray-300"
                              ),
                            }}
                            labelPlacement="outside"
                            placeholder="Select compliance frameworks"
                            selectionMode="multiple"
                            onSelectionChange={(keys) => {
                              const selectedArray = Array.from(keys as Set<string>);
                              setSelectedComplianceFrameworks(selectedArray);
                              setInputs({
                                ...inputs,
                                complianceFrameworks: selectedArray,
                              });
                            }}
                            selectedKeys={new Set(selectedComplianceFrameworks)}
                            renderValue={(items) => {
                              if (items.length === 0) return "";
                              if (items.length === 1) return items[0].textValue;
                              
                              // Create a temporary element to measure text width
                              const measureText = (text: string) => {
                                const canvas = document.createElement('canvas');
                                const context = canvas.getContext('2d');
                                if (context) {
                                  context.font = '14px system-ui, -apple-system, sans-serif';
                                  return context.measureText(text).width;
                                }
                                return text.length * 8; // Fallback estimate
                              };
                              
                              const maxWidth = 280; // Approximate available width in the select
                              
                              // If all items fit, return them all
                              const allText = items.map(item => item.textValue).join(", ");
                              if (measureText(allText) <= maxWidth) {
                                return allText;
                              }
                              
                              // Otherwise, find how many we can fit with "+ X others"
                              let displayText = items[0].textValue;
                              let displayCount = 1;
                              
                              // Try to fit as many frameworks as possible while leaving room for "+ X others"
                              for (let i = 1; i < items.length - 1; i++) {
                                const remaining = items.length - (i + 1);
                                const testText = displayText + ", " + items[i].textValue;
                                const withOthers = `${testText} + ${remaining} others`;
                                
                                if (measureText(withOthers) <= maxWidth) {
                                  displayText = testText;
                                  displayCount++;
                                } else {
                                  break;
                                }
                              }
                              
                              const remaining = items.length - displayCount;
                              return (
                                <span>
                                  {displayText} <span style={{ color: '#006FEE' }}>+ {remaining} others</span>
                                </span>
                              );
                            }}
                          >
                            {complianceFrameworks.map((framework) => (
                              <SelectItem key={framework.value} value={framework.value}>
                                {framework.label}
                              </SelectItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                    );
                  }
                  
                  return input.type === "textarea" ? (
                    <div key={input.key}>
                      <div className="flex items-center mb-1">
                        <span className="text-sm">{input.label}</span>
                      </div>
                      <Textarea
                        placeholder={input.placeholder}
                        value={
                          inputs[input.key] &&
                            typeof inputs[input.key] === "string"
                            ? (inputs[input.key] as string)
                            : ""
                        }
                        onChange={(e) => {
                          setErrors({ ...errors, [input.key]: "" });
                          setInputs({
                            ...inputs,
                            [input.key]: e.target.value.trimStart(),
                          });
                        }}
                        labelPlacement="outside"
                        errorMessage={errors[input.key]}
                        isInvalid={!!errors[input.key]}
                        variant="faded"
                        classNames={{
                          inputWrapper: cn(
                            "w-full shadow-none rounded-md border-1 border-gray-300",
                            !!errors[input.key] && "border-[#F31260]"
                          ),
                        }}
                      />
                    </div>
                  ) : (
                    <ZInput
                      ref={(el) => {
                        if (el) {
                          inputRefs.current[input.key] = el;
                        }
                      }}
                      key={input.key}
                      label={input.label}
                      placeholder={input.placeholder}
                      type={input.type}
                      value={
                        inputs[input.key] &&
                          typeof inputs[input.key] === "string"
                          ? (inputs[input.key] as string)
                          : ""
                      }
                      onChange={(e) => handleInputChange(e, input.key, input.type)}
                      labelPlacement="outside"
                      maxLength={25}
                      errorMessage={errors[input.key]}
                      isInvalid={!!errors[input.key]}
                      variant="faded"
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                      classNames={{
                        inputWrapper: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors[input.key] && "border-[#F31260]"
                        ),
                      }}
                    />
                  );
                })
              : null}

              {/* Display inputs for non-policy management types */}
              {type !== AGENT_TYPES.POLICY_MANAGEMENT && helperData?.inputs?.map(
                (input: {
                  key: string;
                  label: string;
                  placeholder: string;
                  type: string;
                  isRequired: boolean;
                }) => {
                  // Skip rendering the title input for GAP_ASSESSMENT as it's shown after dropdown
                  if (type === AGENT_TYPES.GAP_ASSESSMENT && input.key === "title") {
                    return null;
                  }
                  if (input.type === "textarea") {
                    return (
                      <Textarea
                        key={input.key}
                        label={input.label}
                        placeholder={input.placeholder}
                        value={
                          inputs[input.key] &&
                            typeof inputs[input.key] === "string"
                            ? (inputs[input.key] as string)
                            : ""
                        }
                        onChange={(e) => {
                          setErrors({ ...errors, [input.key]: "" });
                          setInputs({
                            ...inputs,
                            [input.key]: e.target.value.trimStart(),
                          });
                        }}
                        labelPlacement="outside"
                        errorMessage={errors[input.key]}
                        isInvalid={!!errors[input.key]}
                        variant="faded"
                        classNames={{
                          inputWrapper: cn(
                            "w-full shadow-none rounded-md border-1 border-gray-300",
                            !!errors[input.key] && "border-[#F31260]"
                          ),
                        }}
                      />
                    );
                  }

                  if (input.type === "toggle") {
                    return (
                      <div className="" key={input.key}>
                        <div 
                          className="flex items-center cursor-pointer"
                          onClick={() => {
                            const newValue = !((inputs[input.key] && typeof inputs[input.key] === "boolean") ? (inputs[input.key] as boolean) : false);
                            if (input.key === "requestVendor") {
                              // Clear vendor-related fields when toggle is turned off
                              if (!newValue) {
                                setInputs({
                                  ...inputs,
                                  [input.key]: newValue,
                                  vendorEmail: "",
                                  contactEmail: "",
                                  questionnaire_id: "",
                                });
                                // Clear any validation errors for these fields
                                setErrors((prevErrors) => {
                                  const newErrors = { ...prevErrors };
                                  delete newErrors.vendorEmail;
                                  delete newErrors.contactEmail;
                                  return newErrors;
                                });
                              } else {
                                setInputs({
                                  ...inputs,
                                  [input.key]: newValue,
                                });
                              }
                            } else {
                              setInputs({
                                ...inputs,
                                [input.key]: newValue,
                              });
                            }
                          }}
                        >
                          <Switch
                            isSelected={
                              inputs[input.key] &&
                                typeof inputs[input.key] === "boolean"
                                ? (inputs[input.key] as boolean)
                                : false
                            }
                            onValueChange={(isSelected) => {
                              if (input.key === "requestVendor") {
                                // Clear vendor-related fields when toggle is turned off
                                if (!isSelected) {
                                  setInputs({
                                    ...inputs,
                                    [input.key]: isSelected,
                                    vendorEmail: "",
                                    contactEmail: "",
                                    questionnaire_id: "",
                                  });
                                  // Clear any validation errors for these fields
                                  setErrors((prevErrors) => {
                                    const newErrors = { ...prevErrors };
                                    delete newErrors.vendorEmail;
                                    delete newErrors.contactEmail;
                                    return newErrors;
                                  });
                                } else {
                                  setInputs({
                                    ...inputs,
                                    [input.key]: isSelected,
                                  });
                                }
                              } else {
                                setInputs({
                                  ...inputs,
                                  [input.key]: isSelected,
                                });
                              }
                            }}
                            size="sm"
                          >
                            <div className="pl-1 font-medium text-sm">
                              {input.label}
                            </div>
                          </Switch>
                          {input.key === "requestVendor" && (
                            <ZTooltip content="Your assessment will automatically proceed once the vendor submits the details." placement="top" showArrow={true} className="w-[300px]" isDisabled={false}>
                              <div className="flex items-center ml-2 text-gray-500">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            </ZTooltip>
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <ZInput
                      ref={(el) => {
                        if (el) {
                          inputRefs.current[input.key] = el;
                        }
                      }}
                      key={input.key}
                      label={input.label}
                      placeholder={input.placeholder}
                      type={input.type}
                      value={
                        inputs[input.key] &&
                          typeof inputs[input.key] === "string"
                          ? (inputs[input.key] as string)
                          : ""
                      }
                      onChange={(e) => handleInputChange(e, input.key, input.type)}
                      labelPlacement="outside"
                      maxLength={getCharacterLimit(type, input.key)}
                      errorMessage={errors[input.key]}
                      isInvalid={!!errors[input.key]}
                      variant="faded"
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                      classNames={{
                        inputWrapper: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors[input.key] && "border-[#F31260]"
                        ),
                      }}
                    />
                  );
                }
              )}

            </div>
            
            {/* Regular dropdown for non-policy management types */}
            {helperData?.dropdown && type !== AGENT_TYPES.POLICY_MANAGEMENT ? (
              <Select
                key={`dropdown-${type}`}
                classNames={{
                  trigger: cn(
                    "w-full shadow-none rounded-md border-1 border-gray-300",
                    !!errors.session && "border-[#F31260]"
                  ),
                  popoverContent: cn(
                    "w-full shadow-none rounded-md border-1 border-gray-300",
                    !!errors.session && "border-[#F31260]"
                  ),
                  listboxWrapper: cn("custom-dropdown-scrollbar", scrollFadeClasses),
                }}
                items={filteredDropdownOptions}
                label={helperData.dropdown.label}
                labelPlacement="outside"
                selectedKeys={new Set(selectedDropdownOption ? [selectedDropdownOption as string] : [])}
                placeholder={helperData.dropdown.placeholder}
                selectionMode="single"
                isLoading={
                  type === AGENT_TYPES.GAP_ASSESSMENT
                    ? (isLoadingFrameworks || isFetchingFrameworks)
                    : false
                }
                onSelectionChange={(keys) => {
                  const selectedValues = Array.from(keys as Set<string>);
                  if (selectedValues.includes("add_new_framework")) {
                    // Close the upload dialog and open the wizard
                    const sessionTitle = inputs.title as string || "";
                    onAddNewFramework?.(sessionTitle);
                    return;
                  }
                  setSelectedDropdownOption(selectedValues[0]);
                }}
                renderValue={(items) => {
                  if (!items || items.length === 0) return null;

                  const item = items[0];
                  const option = filteredDropdownOptions.find(opt => opt.value === item.key);

                  if (option?.isCustom) {
                    return (
                      <span className="text-sm font-normal text-[#006FEE]">
                        {option.label}
                      </span>
                    );
                  }

                  // For non-custom options, show just the label
                  return <span>{option?.label || item.textValue}</span>;
                }}
                disabledKeys={
                  // Remove disabledKeys for selected options to allow unselecting in multi-select
                  new Set()
                }
              >
                {(item: { value: string; label: string; isCustom?: boolean }) => (
                  <SelectItem
                    key={item.value}
                    value={item.value}
                    className={item.isCustom ? "text-[#006FEE]" : ""}
                  >
                    {item.isCustom ? (
                      <div className="flex flex-row items-center gap-1.5 w-full">
                        <Plus className="w-4 h-4 text-[#006FEE]" strokeWidth={1.5} />
                        <span className="text-sm font-normal text-[#006FEE]">
                          {item.label}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm font-normal">
                        {item.label}
                      </span>
                    )}
                  </SelectItem>
                )}
              </Select>
            ) : null}

            {/* Render inputs that should appear after dropdown for GAP_ASSESSMENT */}
            {type === AGENT_TYPES.GAP_ASSESSMENT && helperData?.inputsAfterDropdown && (
              <div className="mt-4">
                {helperData.inputsAfterDropdown.map(
                  (input: {
                    key: string;
                    label: string;
                    placeholder: string;
                    type: string;
                    isRequired: boolean;
                  }) => {
                    if (input.type === "textarea") {
                      return (
                        <Textarea
                          key={input.key}
                          label={input.label}
                          placeholder={input.placeholder}
                          value={
                            inputs[input.key] &&
                              typeof inputs[input.key] === "string"
                              ? (inputs[input.key] as string)
                              : ""
                          }
                          onChange={(e) => {
                            setErrors({ ...errors, [input.key]: "" });
                            setInputs({
                              ...inputs,
                              [input.key]: e.target.value.trimStart(),
                            });
                          }}
                          labelPlacement="outside"
                          errorMessage={errors[input.key]}
                          isInvalid={!!errors[input.key]}
                          variant="faded"
                          className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                          classNames={{
                            inputWrapper: cn(
                              "w-full shadow-none rounded-md border-1 border-gray-300",
                              !!errors[input.key] && "border-[#F31260]"
                            ),
                          }}
                        />
                      );
                    }

                    if (input.type === "toggle") {
                      return (
                        <div key={input.key} className="">
                          <div 
                            className="flex items-center cursor-pointer"
                            onClick={() => {
                              const newValue = !inputs[input.key];
                              setInputs({ ...inputs, [input.key]: newValue });
                              if (newValue && input.key === "requestVendor" && member) {
                                // Pre-fill contact email with logged-in user email if available
                                setInputs({
                                  ...inputs,
                                  [input.key]: newValue,
                                  contactEmail: member.email_address || "",
                                });
                              }
                            }}
                          >
                            <Switch
                              size="sm"
                              color="primary"
                              isSelected={!!inputs[input.key]}
                              onValueChange={(value) => {
                                setInputs({ ...inputs, [input.key]: value });
                                if (value && input.key === "requestVendor" && member) {
                                  // Pre-fill contact email with logged-in user email if available
                                  setInputs({
                                    ...inputs,
                                    [input.key]: value,
                                    contactEmail: member.email_address || "",
                                  });
                                }
                              }}
                            />
                            <div className="pl-1 font-medium text-sm">{input.label}</div>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <ZInput
                        key={input.key}
                        label={input.label}
                        placeholder={input.placeholder}
                        type={input.type}
                        value={
                          inputs[input.key] &&
                            typeof inputs[input.key] === "string"
                            ? (inputs[input.key] as string)
                            : ""
                        }
                        onChange={(e) => handleInputChange(e, input.key, input.type)}
                        labelPlacement="outside"
                        maxLength={getCharacterLimit(type, input.key)}
                        errorMessage={errors[input.key]}
                        isInvalid={!!errors[input.key]}
                        variant="faded"
                        className={`focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md ${selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 || selectedDropdownOption === RiskAssessmentTypes.NIST_AI_RMF ? 'mb-4' : ''}`}
                        classNames={{
                          inputWrapper: cn(
                            "w-full shadow-none rounded-md border-1 border-gray-300",
                            !!errors[input.key] && "border-[#F31260]"
                          ),
                        }}
                      />
                    );
                  }
                )}
              </div>
            )}
            

            {/* Show toggle only for SOC 2 frameworks in Gap Assessment */}
            {helperData &&
              type === AGENT_TYPES.GAP_ASSESSMENT &&
              (helperData as any)?.toggle &&
              isSocFrameworkSelected && (
                <>
                  <div className="pt-4">
                    <Switch
                      isSelected={isToggleSelected}
                      onValueChange={setIsToggleSelected}
                      size="sm"
                    >
                      <div className="pl-1 font-medium text-sm">
                        {(helperData as any)?.toggle?.label}
                      </div>
                    </Switch>
                  </div>
                  <hr className="mt-2"/>
                </>
              )}
            
            {/* ...existing code for FAIR risk statement... */}
            {selectedDropdownOption === RiskAssessmentTypes.FAIR && (
              <div className="grid w-full items-center gap-1.5">
                <Textarea
                  label="Risk to be evaluated"
                  placeholder="Add risk"
                  value={inputs.risk_statement as string || ""}
                  onChange={(e) => {
                    setErrors({ ...errors, risk: "" });
                    setInputs({
                      ...inputs,
                      risk_statement: e.target.value.trimStart(),
                    });
                  }}
                  labelPlacement="outside"
                  errorMessage={errors.risk}
                  isInvalid={!!errors.risk}
                  variant="faded"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors.risk && "border-[#F31260]"
                    ),
                  }}
                />
              </div>
            )}
            
            {/* Radio buttons only for NIST CSF 2 in Gap Assessment (not AI RMF) */}
            {helperData?.radio && 
              type === AGENT_TYPES.GAP_ASSESSMENT && 
              selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 && (
              <div className="w-full">
                <p className="text-sm font-medium text-gray-700 mb-1">{helperData.radio.label}</p>
                <RadioGroup
                  orientation="horizontal"
                  value={inputs[helperData.radio.key] as string || helperData.radio.options.find((opt: { value: string; label: string; isDefault?: boolean }) => opt.isDefault)?.value || ""}
                  onValueChange={(value) => {
                    setInputs({
                      ...inputs,
                      [helperData.radio.key]: value,
                    });
                  }}
                  size="sm"
                  isDisabled={false}
                  isInvalid={!!errors.session}
                  errorMessage={errors.session}
                >
                  {helperData.radio.options.map((option: { value: string; label: string; isDefault?: boolean }) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </Radio>
                  ))}
                </RadioGroup>
              </div>
            )}
            
            {/* File uploads: Show for NIST frameworks in Gap Assessment only */}
            {(helperData?.uploads &&
              type === AGENT_TYPES.GAP_ASSESSMENT &&
              isNistFrameworkForUploads
            ) && helperData?.uploads?.map?.(
              ({
                title,
                allowedFileTypes,
                type,
                maxFiles,
                sec_ctx
              }: {
                title: string;
                allowedFileTypes: string[];
                type: string;
                maxFiles: number;
                sec_ctx: string;
              }) => (
                <div className="grid w-full items-center gap-1.5" key={title+selectedDropdownOption}>
                  <p className="text-small">{title}</p>
                  <FileUpload
                    sec_ctx={selectedDropdownOption as string}
                    maxFiles={selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 ? 400 : maxFiles}
                    key={title+selectedDropdownOption}
                    isMultiple={true}
                    allowedFileTypes={allowedFileTypes}
                    setFiles={(files: UploadedFile[], type: string) =>
                      handleSetFiles(files, type)
                    }
                    type={type}
                    ref={(el: HTMLDivElement) =>
                      (fileUploadRefs.current[type] = el)
                    }
                  />
                </div>
              )
            )}
            
            {/* Keep existing file uploads for non-Gap Assessment types */}
            {(helperData?.uploads &&
              type !== AGENT_TYPES.GAP_ASSESSMENT &&
              (type !== AGENT_TYPES.RISK_ASSESSMENT ||
               selectedDropdownOption !== RiskAssessmentTypes.NIST_CSF_2 ||
               inputs[helperData.radio?.key] === "all")
            ) && helperData?.uploads?.map?.(
              ({
                title,
                allowedFileTypes,
                type,
                maxFiles,
                sec_ctx
              }: {
                title: string;
                allowedFileTypes: string[];
                type: string;
                maxFiles: number;
                sec_ctx: string;
              }) => (
                <div className="grid w-full items-center gap-1.5" key={title+selectedDropdownOption}>
                  <p className="text-small">
                    {selectedDropdownOption === RiskAssessmentTypes.FAIR
                      ? "Upload Documents (Optional)"
                      : title}
                  </p>
                  <FileUpload
                    sec_ctx={sec_ctx===AGENT_TYPES.RISK_ASSESSMENT? (selectedDropdownOption as string) : sec_ctx}
                    maxFiles={selectedDropdownOption === RiskAssessmentTypes.FAIR ? 5 : 
                              selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 ? 400 : 
                              maxFiles}
                    key={title+selectedDropdownOption}
                    isMultiple={true}
                    allowedFileTypes={allowedFileTypes}
                    setFiles={(files: UploadedFile[], type: string) =>
                      handleSetFiles(files, type)
                    }
                    type={type}
                    ref={(el: HTMLDivElement) =>
                      (fileUploadRefs.current[type] = el)
                    }
                  />
                </div>
              )
            )}
            
            {/* ...existing code for contact email... */}
            {inputs.requestVendor === true && (
                <ZInput
                  key="contactEmail"
                  label={
                    <div className="flex items-center">
                      Contact Email
                      <ZTooltip content="Vendors will contact you at this email if they have questions during submission." placement="right" showArrow={true} isDisabled={false} className="w-[300px]">
                        <div className="flex items-center ml-2 text-gray-500">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </ZTooltip>
                    </div>
                  }
                  placeholder="contact@company.com"
                  type="email"
                  value={(inputs.contactEmail as string) || ""}
                  onChange={(e) => {
                    setErrors({ ...errors, contactEmail: "" });
                    setInputs({
                      ...inputs,
                      contactEmail: e.target.value.trimStart(),
                    });
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value && !isValidEmail(value)) {
                      setErrors({ ...errors, contactEmail: "Please enter a valid email address" });
                    }
                  }}
                  labelPlacement="outside"
                  errorMessage={errors.contactEmail}
                  isInvalid={!!errors.contactEmail}
                  variant="faded"
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors.contactEmail && "border-[#F31260]"
                    ),
                  }}
                  
                />
              )}
          </div>
        </ZModalBody>
        <ZModalFooter>
          <ZButton
            variant="bordered"
            onClick={() => {
              // Cancel uploads before closing
              cleanupUploads();
              setIsOpen({
                isOpen: false,
              });
            }}
            className="px-3 py-1 h-auto text-sm"
            isDisabled={isLoading}
          >
            Cancel
          </ZButton>
          <ZButton
            type="submit"
            onClick={() => {
              handleSubmit().catch(e => {
                console.error(e);
                return;
              });
            }}
            isLoading={isLoading}
            isDisabled={isLoading || buttonDisabled}
            disabled={isLoading || buttonDisabled}
            className="px-3 py-1 h-auto text-sm"
          >
            Submit
          </ZButton>
        </ZModalFooter>
      </ZModalContent>
    </ZModal>
  );
}
